import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  gerente: "Gerente — vê tudo (custo, lucro, fornecedores)",
  colaborador: "Colaborador — não vê custos, lucros nem fornecedores",
  tecnico: "Técnico — acesso somente ao módulo Assistência (sem vendas, pedidos ou catálogo)",
  marketing_crm: "Marketing CRM — acesso somente ao módulo CRM (não libera mais nada do sistema)",
};

const BLANK_FORM = {
  id: null, name: "", email: "", password: "",
  role: "colaborador", can_create_products: false, crm_can_edit: false,
};

export default function Funcionarios() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/employees");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const isEditing = !!form.id;

  const submit = async () => {
    if (!isEditing && (!form.email || !form.password)) return toast.error("Preencha email e senha");
    setBusy(true);
    try {
      if (isEditing) {
        // PATCH — só envia campos relevantes
        const body = {
          name: form.name,
          role: form.role,
          can_create_products: !!form.can_create_products,
          crm_can_edit: !!form.crm_can_edit,
        };
        if (form.password) body.password = form.password;
        await api.put(`/employees/${form.id}`, body);
        toast.success("Funcionário atualizado");
      } else {
        await api.post("/employees", form);
        toast.success("Funcionário criado");
      }
      setOpen(false);
      setForm(BLANK_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir funcionário?")) return;
    await api.delete(`/employees/${id}`);
    load();
  };

  const startEdit = (e) => {
    setForm({
      id: e.id,
      name: e.name || "",
      email: e.email,
      password: "",
      role: e.role,
      can_create_products: !!e.can_create_products,
      crm_can_edit: !!e.crm_can_edit,
    });
    setOpen(true);
  };

  const openNew = () => { setForm(BLANK_FORM); setOpen(true); };

  return (
    <div className="space-y-8" data-testid="funcionarios-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="label-tiny mb-2">Equipe</p>
          <h1 className="font-display text-4xl tracking-tight">Funcionários</h1>
          <p className="text-sm text-neutral-500 mt-1">Até 5 funcionários neste plano. {list.length}/5 usados.</p>
        </div>
        <Button onClick={openNew} disabled={list.length >= 5} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-employee-button">
          <Plus size={16} className="mr-1"/> Novo funcionário
        </Button>
      </div>

      <div className="space-y-3">
        {list.map(e => (
          <Card key={e.id} className="p-4 border-[#E5E5E5] shadow-none flex items-center justify-between gap-3" data-testid={`employee-${e.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{e.name || e.email}</div>
              <div className="text-xs text-neutral-500 truncate">{e.email}</div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-xs inline-block px-2 py-0.5 rounded-full border border-[#E5E5E5] bg-neutral-50">{e.role === "gerente" ? "Gerente" : e.role === "tecnico" ? "Técnico" : e.role === "marketing_crm" ? "Marketing CRM" : "Colaborador"}</span>
                {e.role === "colaborador" && e.can_create_products && (
                  <span className="text-[10px] inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`emp-cadprod-flag-${e.id}`}>
                    Cadastra produtos
                  </span>
                )}
                {e.role === "marketing_crm" && (
                  <span className={`text-[10px] inline-block px-2 py-0.5 rounded-full border ${e.crm_can_edit ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-neutral-50 text-neutral-600 border-neutral-200"}`} data-testid={`emp-crm-flag-${e.id}`}>
                    {e.crm_can_edit ? "Pode editar" : "Apenas visualizar"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={()=>startEdit(e)} data-testid={`edit-emp-${e.id}`}><Pencil size={14}/></Button>
              <Button size="sm" variant="ghost" onClick={()=>remove(e.id)} data-testid={`del-emp-${e.id}`}><Trash2 size={14}/></Button>
            </div>
          </Card>
        ))}
        {list.length === 0 && <div className="text-center py-12 text-sm text-neutral-500">Nenhum funcionário cadastrado.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{isEditing ? "Editar funcionário" : "Novo funcionário"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="emp-name-input"/></div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} disabled={isEditing} data-testid="emp-email-input"/>
              {isEditing && <p className="text-[10px] text-neutral-400 mt-0.5">Email não pode ser alterado</p>}
            </div>
            <div>
              <Label>{isEditing ? "Nova senha (opcional)" : "Senha"}</Label>
              <Input value={form.password} onChange={e=>setForm({...form, password: e.target.value})} placeholder={isEditing ? "Deixe em branco para manter" : ""} data-testid="emp-password-input"/>
            </div>
            <div>
              <Label>Cargo</Label>
              <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                <SelectTrigger data-testid="emp-role-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="colaborador">Colaborador</SelectItem>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                  <SelectItem value="marketing_crm">Marketing CRM</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">{ROLE_LABEL[form.role]}</p>
            </div>
            {form.role === "colaborador" && (
              <Card className="p-3 border-[#E5E5E5] shadow-none bg-neutral-50">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="cad-prod"
                    checked={!!form.can_create_products}
                    onCheckedChange={(v) => setForm({ ...form, can_create_products: !!v })}
                    data-testid="emp-can-create-products"
                  />
                  <div className="flex-1">
                    <Label htmlFor="cad-prod" className="cursor-pointer text-sm font-medium">
                      Liberar Cadastro de Produto
                    </Label>
                    <p className="text-[11px] text-neutral-600 mt-1 leading-snug">
                      Quando ativada, o colaborador pode <b>criar, editar e excluir produtos</b> e ver fornecedores ao cadastrar. <b>Não libera</b> Financeiro, lucros, margens ou relatórios.
                    </p>
                  </div>
                </div>
              </Card>
            )}
            {form.role === "marketing_crm" && (
              <Card className="p-3 border-[#E5E5E5] shadow-none bg-neutral-50 space-y-2">
                <p className="text-[11px] text-neutral-600 leading-snug">
                  Este cargo libera <b>apenas o módulo CRM</b>. Não libera nenhuma outra função do sistema.
                </p>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="emp-crm-edit-yes">
                  <input
                    type="radio"
                    name="crm-perm"
                    className="mt-0.5"
                    checked={!!form.crm_can_edit}
                    onChange={()=>setForm({ ...form, crm_can_edit: true })}
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Permitir editar</span>
                    <p className="text-[11px] text-neutral-600 leading-snug">Pode fazer a mesma coisa que o administrador <i>dentro do CRM</i> (criar, editar, mover, excluir leads e observações).</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="emp-crm-edit-no">
                  <input
                    type="radio"
                    name="crm-perm"
                    className="mt-0.5"
                    checked={!form.crm_can_edit}
                    onChange={()=>setForm({ ...form, crm_can_edit: false })}
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Apenas visualizar</span>
                    <p className="text-[11px] text-neutral-600 leading-snug">Pode apenas <b>visualizar e filtrar</b>, mas não pode editar absolutamente nada.</p>
                  </div>
                </label>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-emp-button">
              {busy ? "Salvando…" : isEditing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
